/**
 * The Ads report's rows: each ad the server returned (getAnalytics `ads` section), with the
 * name a person reads and where its clicks came from. Placement shares are of CLICKS (or of
 * visits when an ad has visits but no recorded clicks) — never of the two added together.
 */
import { campaignDisplay } from './campaignDisplay';

export function adRows(ads, campaigns) {
  return ((ads && ads.rows) || []).map((a) => {
    const name = campaignDisplay(a.campaign, campaigns);
    const by = a.clicks ? 'clicks' : 'sessions';
    const whole = a[by] || 0;
    const placements = Object.entries(a.placements || {})
      .map(([label, p]) => ({ label, value: p[by] || 0 }))
      .filter((p) => p.value > 0)
      .sort((x, y) => y.value - x.value)
      .map((p) => ({ ...p, share: whole ? p.value / whole : 0 }));
    return {
      ...a,
      label: name.label,
      named: name.kind !== 'opaque',
      placements,
      placementsOf: by === 'clicks' ? 'clicks' : 'visits',
    };
  });
}

/** The ways the report can be ordered. The first is the default: like for like. */
export const AD_ORDERS = [
  { value: 'week', label: 'First week' },
  { value: 'day1', label: 'First day' },
  { value: 'clicks', label: 'Clicks in period' },
];

/**
 * Ads in the chosen order, biggest first.
 *
 * "First week" and "First day" count from each ad's OWN launch, so an ad that has run 7 days
 * can be compared with one that has run 30 — a period total favours whichever ran longest.
 * An ad with no launch figures (not a Meta ad, or not loaded yet) goes last, never first.
 *
 * @param rows   adRows() output
 * @param starts { [campaign]: { day1, week, … } } from useAdStarts
 * @param order  'week' | 'day1' | 'clicks'
 */
export function orderAds(rows, starts = {}, order = 'week') {
  const byClicks = (a, b) => b.clicks - a.clicks || b.sessions - a.sessions || a.campaign.localeCompare(b.campaign);
  if (order === 'clicks') return [...rows].sort(byClicks);
  const value = (a) => {
    const s = starts[a.campaign];
    return s && s.launchMs != null && !s.capped ? s[order] : null;
  };
  return [...rows].sort((a, b) => {
    const x = value(a); const y = value(b);
    if (x == null && y == null) return byClicks(a, b);
    if (x == null) return 1;
    if (y == null) return -1;
    return y - x || byClicks(a, b);
  });
}

/**
 * Which Meta ads still lack something an admin can add: a name, the Ad Library link, the
 * wording or the picture.
 */
export function adsNeedingDetails(rows, images = {}, texts = {}) {
  return rows.filter((a) => a.metaAd).map((a) => ({
    ...a,
    missing: {
      name: !a.named,
      link: !a.adLibraryId,
      text: !texts[a.campaign],
      picture: !images[a.campaign],
    },
  })).filter((a) => Object.values(a.missing).some(Boolean));
}
